import { useState, useEffect, useRef } from 'react'
import { Plus, Package, Search, ChevronDown, StickyNote, FileEdit, FileUp } from 'lucide-react'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
  arrayMove,
} from '@dnd-kit/sortable'
import { Voce, ListinoItem, VoceUpdate, VoceLiberaCreate } from '../../types/offerte'
import { SortableVoceRow } from './SortableVoceRow'

interface ValoriSectionProps {
  voci: Voce[]
  listino: ListinoItem[]
  totaleValore: number
  subtotaleVoci: number
  scontoFinalePercentuale: number
  onAddVoce: (listinoId: string, quantita: number) => void
  onAddNota: (descrizione: string) => void
  onAddVoceLibera: (data: VoceLiberaCreate) => void
  onUpdateVoce: (voceId: string, updates: VoceUpdate) => void
  onUpdateNota: (voceId: string, descrizione: string) => void
  onDeleteVoce: (voceId: string) => void
  onReorder: (voceIds: string[]) => void
  onScontoFinaleChange: (sconto: number) => void
  onImportDocx?: (file: File) => Promise<void>
  disabled?: boolean
  integrativaLabels?: Record<string, string>
}

export function ValoriSection({
  voci,
  listino,
  totaleValore,
  subtotaleVoci,
  scontoFinalePercentuale,
  onAddVoce,
  onAddNota,
  onAddVoceLibera,
  onUpdateVoce,
  onUpdateNota,
  onDeleteVoce,
  onReorder,
  onScontoFinaleChange,
  onImportDocx,
  disabled,
  integrativaLabels = {},
}: ValoriSectionProps) {
  const [showAddForm, setShowAddForm] = useState(false)
  const [showNotaForm, setShowNotaForm] = useState(false)
  const [showVoceLiberaForm, setShowVoceLiberaForm] = useState(false)
  const [selectedListino, setSelectedListino] = useState<ListinoItem | null>(null)
  const [quantita, setQuantita] = useState('1')
  const [searchListino, setSearchListino] = useState('')
  const [showListinoDropdown, setShowListinoDropdown] = useState(false)
  const [notaText, setNotaText] = useState('')
  const [localScontoFinale, setLocalScontoFinale] = useState(String(scontoFinalePercentuale || '0'))
  const dropdownRef = useRef<HTMLDivElement>(null)
  const importFileRef = useRef<HTMLInputElement>(null)
  const [importingDocx, setImportingDocx] = useState(false)

  const handleImportDocxClick = () => {
    importFileRef.current?.click()
  }

  const handleImportFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !onImportDocx) return
    setImportingDocx(true)
    try {
      await onImportDocx(file)
    } finally {
      setImportingDocx(false)
      e.target.value = ''
    }
  }

  // Voce libera form state
  const [voceLiberaForm, setVoceLiberaForm] = useState({
    descrizione: '',
    unita_misura: 'pz',
    prezzo_unitario: '',
    quantita: '1',
    sconto_percentuale: '0',
  })

  // Sync sconto finale da props
  useEffect(() => {
    setLocalScontoFinale(String(scontoFinalePercentuale || '0'))
  }, [scontoFinalePercentuale])

  const formatCurrency = (value: number | string) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(Number(value) || 0)
  }

  // DND sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  // Filtra listino attivo e non gia' presente
  const vociIds = new Set(voci.filter(v => v.tipo === 'voce').map(v => v.listino_id))
  const availableListino = listino.filter(l =>
    l.attivo &&
    !vociIds.has(l.id) &&
    (l.codice.toLowerCase().includes(searchListino.toLowerCase()) ||
      l.descrizione.toLowerCase().includes(searchListino.toLowerCase()))
  )

  // Chiudi dropdown quando si clicca fuori
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowListinoDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleAdd = () => {
    if (!selectedListino || !quantita) return
    onAddVoce(selectedListino.id, parseFloat(quantita))
    setSelectedListino(null)
    setQuantita('1')
    setSearchListino('')
    setShowAddForm(false)
  }

  const handleAddNota = () => {
    if (!notaText.trim()) return
    onAddNota(notaText.trim())
    setNotaText('')
    setShowNotaForm(false)
  }

  const handleAddVoceLibera = () => {
    const prezzo = parseFloat(voceLiberaForm.prezzo_unitario)
    const qta = parseFloat(voceLiberaForm.quantita)
    const sconto = parseFloat(voceLiberaForm.sconto_percentuale) || 0
    if (!voceLiberaForm.descrizione.trim() || isNaN(prezzo) || isNaN(qta)) return

    onAddVoceLibera({
      descrizione: voceLiberaForm.descrizione.trim(),
      unita_misura: voceLiberaForm.unita_misura || 'pz',
      prezzo_unitario: prezzo,
      quantita: qta,
      sconto_percentuale: sconto,
    })
    setVoceLiberaForm({
      descrizione: '',
      unita_misura: 'pz',
      prezzo_unitario: '',
      quantita: '1',
      sconto_percentuale: '0',
    })
    setShowVoceLiberaForm(false)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = voci.findIndex(v => v.id === active.id)
    const newIndex = voci.findIndex(v => v.id === over.id)
    const newOrder = arrayMove(voci, oldIndex, newIndex)
    onReorder(newOrder.map(v => v.id))
  }

  const handleScontoFinaleBlur = () => {
    const s = parseFloat(localScontoFinale)
    if (!isNaN(s)) {
      onScontoFinaleChange(s)
    } else {
      setLocalScontoFinale(String(scontoFinalePercentuale || '0'))
    }
  }

  const scontoFinaleVal = Number(scontoFinalePercentuale) || 0
  const importoSconto = subtotaleVoci * scontoFinaleVal / 100

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Package className="w-5 h-5 text-green-500" />
          Valore Offerta
        </h3>
        {!disabled && (
          <div className="flex items-center gap-2">
            {onImportDocx && (
              <>
                <input
                  ref={importFileRef}
                  type="file"
                  accept=".docx"
                  className="hidden"
                  onChange={handleImportFileChange}
                />
                <button
                  onClick={handleImportDocxClick}
                  disabled={importingDocx}
                  className="flex items-center gap-1 text-sm text-teal-600 hover:text-teal-700 disabled:opacity-50"
                  title="Importa voci da file .docx"
                >
                  <FileUp className="w-4 h-4" />
                  {importingDocx ? 'Importazione...' : 'Importa Docx'}
                </button>
              </>
            )}
            <button
              onClick={() => { setShowNotaForm(true); setShowAddForm(false); setShowVoceLiberaForm(false) }}
              className="flex items-center gap-1 text-sm text-amber-600 hover:text-amber-700"
            >
              <StickyNote className="w-4 h-4" />
              Nota
            </button>
            <button
              onClick={() => { setShowVoceLiberaForm(true); setShowAddForm(false); setShowNotaForm(false) }}
              className="flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-700"
            >
              <FileEdit className="w-4 h-4" />
              Voce Libera
            </button>
            <button
              onClick={() => { setShowAddForm(true); setShowNotaForm(false); setShowVoceLiberaForm(false) }}
              className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
            >
              <Plus className="w-4 h-4" />
              Voce
            </button>
          </div>
        )}
      </div>

      {/* Lista Voci con DND */}
      {voci.length === 0 ? (
        <p className="text-sm text-gray-400 py-4 text-center">
          Nessuna voce inserita. Aggiungi voci dal listino o voci libere.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="py-2 w-8"></th>
                  <th className="py-2 text-left text-xs font-medium text-gray-500 uppercase">Codice</th>
                  <th className="py-2 text-left text-xs font-medium text-gray-500 uppercase">Descrizione</th>
                  <th className="py-2 text-right text-xs font-medium text-gray-500 uppercase w-20">Q.ta</th>
                  <th className="py-2 text-left text-xs font-medium text-gray-500 uppercase w-16">U.M.</th>
                  <th className="py-2 text-right text-xs font-medium text-gray-500 uppercase w-24">Prezzo</th>
                  <th className="py-2 text-right text-xs font-medium text-gray-500 uppercase w-20">Sc. %</th>
                  <th className="py-2 text-right text-xs font-medium text-gray-500 uppercase w-24">Totale</th>
                  {!disabled && <th className="py-2 w-10"></th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <SortableContext
                  items={voci.map(v => v.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {voci.map((voce) => (
                    <SortableVoceRow
                      key={voce.id}
                      voce={voce}
                      disabled={!!disabled || !!voce.integrativa_origin_id}
                      onUpdateQuantita={(id, q) => onUpdateVoce(id, { quantita: q })}
                      onUpdateSconto={(id, s) => onUpdateVoce(id, { sconto_percentuale: s })}
                      onUpdateNota={onUpdateNota}
                      onUpdateVoce={onUpdateVoce}
                      onDelete={onDeleteVoce}
                      formatCurrency={formatCurrency}
                      integrativaLabel={voce.integrativa_origin_id ? (integrativaLabels[voce.integrativa_origin_id] || 'INT') : undefined}
                    />
                  ))}
                </SortableContext>
              </tbody>
            </table>
          </DndContext>
        </div>
      )}

      {/* Form aggiunta nota */}
      {showNotaForm && (
        <div className="mt-4 p-4 border border-amber-200 rounded-lg bg-amber-50/50">
          <label className="block text-xs text-gray-500 mb-1">Testo nota / sezione</label>
          <input
            type="text"
            value={notaText}
            onChange={(e) => setNotaText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAddNota() }}
            placeholder="es. IMPIANTO ELETTRICO, Note aggiuntive..."
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
            autoFocus
          />
          <div className="flex justify-end gap-2 mt-2">
            <button
              onClick={() => { setShowNotaForm(false); setNotaText('') }}
              className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-200 rounded"
            >
              Annulla
            </button>
            <button
              onClick={handleAddNota}
              disabled={!notaText.trim()}
              className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50"
            >
              Aggiungi Nota
            </button>
          </div>
        </div>
      )}

      {/* Form aggiunta voce libera */}
      {showVoceLiberaForm && (
        <div className="mt-4 p-4 border border-indigo-200 rounded-lg bg-indigo-50/50">
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-4">
              <label className="block text-xs text-gray-500 mb-1">Descrizione *</label>
              <input
                type="text"
                value={voceLiberaForm.descrizione}
                onChange={(e) => setVoceLiberaForm({ ...voceLiberaForm, descrizione: e.target.value })}
                placeholder="Descrizione voce..."
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                autoFocus
              />
            </div>
            <div className="col-span-1">
              <label className="block text-xs text-gray-500 mb-1">U.M.</label>
              <input
                type="text"
                value={voceLiberaForm.unita_misura}
                onChange={(e) => setVoceLiberaForm({ ...voceLiberaForm, unita_misura: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Prezzo *</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={voceLiberaForm.prezzo_unitario}
                onChange={(e) => setVoceLiberaForm({ ...voceLiberaForm, prezzo_unitario: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Quantita *</label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={voceLiberaForm.quantita}
                onChange={(e) => setVoceLiberaForm({ ...voceLiberaForm, quantita: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Sconto %</label>
              <input
                type="number"
                step="0.5"
                value={voceLiberaForm.sconto_percentuale}
                onChange={(e) => setVoceLiberaForm({ ...voceLiberaForm, sconto_percentuale: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
            </div>
            <div className="col-span-1 flex items-end">
              <span className="text-xs text-gray-400 pb-2">
                {(() => {
                  const p = parseFloat(voceLiberaForm.prezzo_unitario)
                  const q = parseFloat(voceLiberaForm.quantita)
                  const s = parseFloat(voceLiberaForm.sconto_percentuale) || 0
                  if (!isNaN(p) && !isNaN(q)) {
                    return formatCurrency(p * q * (1 - s / 100))
                  }
                  return '-'
                })()}
              </span>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button
              onClick={() => {
                setShowVoceLiberaForm(false)
                setVoceLiberaForm({ descrizione: '', unita_misura: 'pz', prezzo_unitario: '', quantita: '1', sconto_percentuale: '0' })
              }}
              className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-200 rounded"
            >
              Annulla
            </button>
            <button
              onClick={handleAddVoceLibera}
              disabled={!voceLiberaForm.descrizione.trim() || !voceLiberaForm.prezzo_unitario}
              className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Aggiungi Voce Libera
            </button>
          </div>
        </div>
      )}

      {/* Form aggiunta voce da listino */}
      {showAddForm && (
        <div className="mt-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
          <div className="grid grid-cols-12 gap-3">
            {/* Selezione Listino */}
            <div className="col-span-8 relative" ref={dropdownRef}>
              <label className="block text-xs text-gray-500 mb-1">Voce Listino</label>
              <button
                type="button"
                onClick={() => setShowListinoDropdown(!showListinoDropdown)}
                className="w-full flex items-center justify-between px-3 py-2 border border-gray-200 rounded-lg text-left bg-white hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <span className={selectedListino ? 'text-gray-900' : 'text-gray-400'}>
                  {selectedListino ? `${selectedListino.codice} - ${selectedListino.descrizione}` : 'Seleziona voce...'}
                </span>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showListinoDropdown ? 'rotate-180' : ''}`} />
              </button>

              {showListinoDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-hidden">
                  <div className="p-2 border-b border-gray-100">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Cerca nel listino..."
                        value={searchListino}
                        onChange={(e) => setSearchListino(e.target.value)}
                        className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                        autoFocus
                      />
                    </div>
                  </div>

                  <div className="max-h-48 overflow-y-auto">
                    {availableListino.length === 0 ? (
                      <div className="px-3 py-4 text-sm text-gray-500 text-center">
                        Nessuna voce disponibile
                      </div>
                    ) : (
                      availableListino.map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => {
                            setSelectedListino(item)
                            setShowListinoDropdown(false)
                            setSearchListino('')
                          }}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex justify-between items-center"
                        >
                          <div>
                            <span className="font-mono text-xs bg-gray-100 px-1 rounded mr-2">{item.codice}</span>
                            <span className="text-gray-700">{item.descrizione}</span>
                          </div>
                          <span className="text-gray-500">{formatCurrency(item.prezzo_unitario)}</span>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Quantita */}
            <div className="col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Quantita</label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={quantita}
                onChange={(e) => setQuantita(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
            </div>

            {/* Prezzo preview */}
            <div className="col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Prezzo</label>
              <div className="px-3 py-2 bg-gray-100 rounded-lg text-sm text-gray-600">
                {selectedListino ? formatCurrency(selectedListino.prezzo_unitario) : '-'}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-3">
            <button
              onClick={() => {
                setShowAddForm(false)
                setSelectedListino(null)
                setQuantita('1')
              }}
              className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-200 rounded"
            >
              Annulla
            </button>
            <button
              onClick={handleAdd}
              disabled={!selectedListino}
              className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Aggiungi
            </button>
          </div>
        </div>
      )}

      {/* Totali */}
      <div className="border-t border-gray-200 mt-4 pt-4 space-y-2">
        {scontoFinaleVal !== 0 && (
          <>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Subtotale voci</span>
              <span className="font-medium text-gray-700">{formatCurrency(subtotaleVoci)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">
                {scontoFinaleVal > 0 ? 'Sconto' : 'Maggiorazione'} ({Math.abs(scontoFinaleVal).toFixed(1)}%)
              </span>
              <span className={`font-medium ${scontoFinaleVal > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {scontoFinaleVal > 0 ? '-' : '+'}{formatCurrency(Math.abs(importoSconto))}
              </span>
            </div>
          </>
        )}

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-semibold text-gray-900">TOTALE VALORE</span>
            {!disabled && (
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <span>Sc/Magg. finale:</span>
                <input
                  type="number"
                  step="0.5"
                  value={localScontoFinale}
                  onChange={(e) => setLocalScontoFinale(e.target.value)}
                  onBlur={handleScontoFinaleBlur}
                  className="w-16 px-1.5 py-0.5 text-right border border-gray-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-primary-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <span>%</span>
              </div>
            )}
          </div>
          <span className="text-xl font-bold text-green-600">{formatCurrency(totaleValore)}</span>
        </div>
      </div>
    </div>
  )
}
